// @ts-check
/* ═══════════════════════ LIVE LOCATION (V8) ═══════════════════════
   Opt-in deling av posisjon mellom familiemedlemmer via Supabase realtime.
   Skriver egen posisjon til live_locations-tabellen ~30s.
   Lytter etter andres posisjoner via realtime-channel.
   ═══════════════════════════════════════════════════════════════════ */

import { S } from './state.js';
import { getSB, SB_READY, showToast } from './cloud.js';
import { haptic } from './utils.js';

const WRITE_INTERVAL_MS = 30000;
const STALE_AFTER_MS    = 5 * 60 * 1000; // 5 min uten oppdatering = "stale"

let watchId = null;
let writeTimer = null;
let realtimeSub = null;
let lastPos = null;
let listeners = new Set();

/** Andre brukeres siste kjente posisjon: { name → {lat,lng,accuracy,updated_at} } */
export const peers = new Map();

function notify(){
  for (const fn of listeners) {
    try { fn(); } catch {}
  }
}

export function onPeersChange(fn){
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function isLiveSharing(){
  return watchId !== null;
}

async function writePosition(){
  if (!lastPos || !SB_READY) return;
  const sb = getSB();
  const name = (S.diaryAuthor || '').trim();
  if (!name) return;
  const battery = await getBatteryPct();
  const row = {
    user_name: name,
    lat: lastPos.lat,
    lng: lastPos.lng,
    accuracy: Math.round(lastPos.accuracy || 0),
    speed: lastPos.speed ?? null,
    heading: lastPos.heading ?? null,
    battery,
    updated_at: new Date().toISOString(),
  };
  try {
    await sb.from('live_locations').upsert(row, { onConflict: 'user_name' });
  } catch (e) {
    // tabellen kan mangle — feil rapporteres bare første gang
    console.warn('[liveLocation] upsert feilet:', e.message);
  }
}

async function getBatteryPct(){
  try {
    if (!navigator.getBattery) return null;
    const b = await navigator.getBattery();
    return Math.round(b.level * 100);
  } catch { return null; }
}

async function loadInitialPeers(){
  if (!SB_READY) return;
  try {
    const sb = getSB();
    const { data, error } = await sb.from('live_locations').select('*');
    if (error) throw error;
    const me = (S.diaryAuthor || '').trim();
    peers.clear();
    for (const row of data || []) {
      if (row.user_name === me) continue;
      peers.set(row.user_name, row);
    }
    notify();
  } catch (e) {
    console.warn('[liveLocation] kunne ikke hente peers:', e.message);
  }
}

function subscribeRealtime(){
  if (!SB_READY || realtimeSub) return;
  const sb = getSB();
  const me = (S.diaryAuthor || '').trim();
  realtimeSub = sb.channel('live_locations_ch')
    .on('postgres_changes',
        { event: '*', schema: 'public', table: 'live_locations' },
        (payload) => {
          const row = payload.new || payload.old;
          if (!row || row.user_name === me) return;
          if (payload.eventType === 'DELETE') peers.delete(row.user_name);
          else peers.set(row.user_name, payload.new);
          notify();
        })
    .subscribe();
}

function unsubscribeRealtime(){
  if (realtimeSub) {
    try { realtimeSub.unsubscribe(); } catch {}
    realtimeSub = null;
  }
}

/** Start live deling. Trenger S.diaryAuthor satt + Supabase. */
export async function startLiveSharing(){
  if (watchId !== null) return true;
  if (!('geolocation' in navigator)) {
    showToast('GPS støttes ikke i denne nettleseren');
    return false;
  }
  if (!SB_READY) {
    showToast('Supabase ikke konfigurert');
    return false;
  }
  if (!(S.diaryAuthor || '').trim()) {
    showToast('Velg navn i Dagbok først');
    return false;
  }

  return new Promise((resolve) => {
    watchId = navigator.geolocation.watchPosition(
      (pos) => {
        lastPos = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          speed: pos.coords.speed,
          heading: pos.coords.heading,
        };
        // Første fix: skriv straks + start subscribers
        if (writeTimer === null) {
          writePosition();
          writeTimer = setInterval(writePosition, WRITE_INTERVAL_MS);
          loadInitialPeers();
          subscribeRealtime();
          haptic('success');
          showToast('🟢 Deler posisjon med familien');
          try { localStorage.setItem('liveShare_on', '1'); } catch {}
          resolve(true);
        }
      },
      (err) => {
        if (writeTimer === null) {
          showToast('GPS feilet: ' + err.message);
          watchId = null;
          resolve(false);
        }
      },
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 30000 }
    );
  });
}

export function stopLiveSharing(){
  if (watchId !== null) {
    try { navigator.geolocation.clearWatch(watchId); } catch {}
    watchId = null;
  }
  if (writeTimer !== null) {
    clearInterval(writeTimer);
    writeTimer = null;
  }
  unsubscribeRealtime();
  lastPos = null;
  try { localStorage.removeItem('liveShare_on'); } catch {}
  haptic('medium');
  showToast('🔴 Stoppet posisjonsdeling');

  // Slett egen rad
  if (SB_READY) {
    const me = (S.diaryAuthor || '').trim();
    if (me) {
      try { getSB().from('live_locations').delete().eq('user_name', me); } catch {}
    }
  }
}

export function toggleLiveSharing(){
  if (isLiveSharing()) {
    stopLiveSharing();
    return false;
  } else {
    startLiveSharing();
    return true;
  }
}

/** Auto-resume hvis bruker hadde det på sist. */
export function maybeResumeLiveSharing(){
  try {
    if (localStorage.getItem('liveShare_on') === '1') {
      // Vent litt så app er klar
      setTimeout(() => { startLiveSharing(); }, 2000);
    }
  } catch {}
}

/** Format peers for UI: navn, alder på siste oppd. */
export function getPeersFresh(){
  const out = [];
  const now = Date.now();
  for (const [name, p] of peers) {
    const age = now - new Date(p.updated_at).getTime();
    out.push({
      name,
      lat: p.lat,
      lng: p.lng,
      accuracy: p.accuracy,
      battery: p.battery,
      ageMin: Math.round(age / 60000),
      stale: age > STALE_AFTER_MS,
    });
  }
  return out;
}
